import _ from "lodash";
import React, {
  useState,
  useCallback,
  useRef,
  useMemo,
  useEffect,
} from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";

import withLoader from "UIComponents/GqlWithLoader";
import Loading from "UIComponents/Loading";
import { useDebouncedCallback } from "Utils/CustomHooks";
import { useEffectEvent, useI18n } from "Hooks";
import { useDispatch } from "Hooks/useDispatch";
import { compose } from "lib/apolloClient";
import { getBothWaysMessagesEnhancer } from "MessagingV2/modules/MessagingEnhancers";
import { useChatOptions } from "MessagingV2/providers/ChatOptionsProvider";
import {
  createMessage,
  getMessagingV2ReduxState,
  addDraftAttachments,
  updateDraft,
  clearDraft,
  updateMessage,
  generateSummary,
  createPollMessage,
} from "MessagingV2/modules/MessagingModule";
import {
  CHAT_TYPE,
  getChatContainer,
  getInitialDraftData,
  addAttachmentsInDraftState,
  getIsViewMode,
  PAGINATION_LIMITS,
  MESSAGE_TYPE,
  isMessagingDisabledForStudent,
  isMessagingDisabledForFamily,
} from "MessagingV2/modules/Utils";
import { getIsStaff, getUserType } from "modules/CommonSelectors";

import EmptyChatContainer from "../EmptyChatContainer";
import MessagesContainer from "../MessagesContainer";
import Footer from "../Footer";
import classes from "./MessagesAndInputContainer.scss";
import { scrollToWithAnimation } from "Utils";
import { InformationFilled } from "@toddle-edu/ds-icons";
import { Tooltip } from "@toddle-edu/ds-web";
import ConditionalWrapper from "Utils/ConditionalWrapper";
import { PARENT, STUDENT } from "Constants/stringConstants";
import { getIsModuleSettingsV2Enabled } from "Utils/ACLStoreUtils";

const MAX_BANNER_MESSAGE_LENGTH = 200;

const getBannerMessage = ({
  bannerMessage,
  isChat,
  personalChatReceiverUser,
  isConnectEnabledForReceiver,
  t,
}) => {
  if (bannerMessage) return { displayBannerMessage: bannerMessage };

  const isModuleSettingV2Enabled = getIsModuleSettingsV2Enabled();
  if (personalChatReceiverUser && isChat && getIsStaff()) {
    const userType = _.get(personalChatReceiverUser, "type");

    if (_.isEqual(userType, STUDENT)) {
      const gradeId = _.get(personalChatReceiverUser, "yearGroup.grade.id");
      const isPersonalChatDisabledForUser = isMessagingDisabledForStudent(
        gradeId
      );

      if (
        isModuleSettingV2Enabled
          ? !isConnectEnabledForReceiver
          : isPersonalChatDisabledForUser
      ) {
        return {
          displayBannerMessage: t("common:messaging_disabled_module_subtext"),
          displayBannerHeader: t("common:messaging_disabled_for_this_student"),
        };
      }
    } else if (_.isEqual(userType, PARENT)) {
      const children = _.get(personalChatReceiverUser, "children", []);
      const gradeIds = _.map(children, child =>
        _.get(child, "yearGroup.grade.id")
      );
      const isPersonalChatDisabledForUser = isMessagingDisabledForFamily(
        gradeIds
      );

      if (
        isModuleSettingV2Enabled
          ? !isConnectEnabledForReceiver
          : isPersonalChatDisabledForUser
      ) {
        return {
          displayBannerMessage: t("common:messaging_disabled_module_subtext"),
          displayBannerHeader: t("common:messaging_disabled_for_this_family"),
        };
      }
    }
  }

  return {};
};

const MessagesAndInputContainer = props => {
  const {
    otherUsers,
    users,
    chatType,
    screenType,
    isArchived,
    savedDraftMessage,
    lastActiveAt,
    renderHeader,
    // message query related props
    conversations,
    loadNextMoreMessages,
    loadPreviousMoreMessages,
    hasNextPage,
    hasPreviousPage,
    goToMessage,
    highlightedMessageId,
    channelLabel,
    description,
    bannerMessage,
    isArchivedChatDetail,
    viewOnlyRoles,
    viewOnlyUsers,
    conversationType,
    isUserCreator,
    isSearchingInOpenedChat,
    currentUserType,
    lastReadMessage,
    unreadMessageCount,
    isPersonalChatPaid,
    createdBy,
    personalChatReceiverUser,
    isConnectEnabledForReceiver,
    archivedBy,
  } = props;

  const { chatId, screenMode } = useChatOptions();
  const isViewMode = getIsViewMode({ screenMode });

  const dispatch = useDispatch();
  const chatPanelRef = useRef(); // needed to refer RTE input in both children components

  const { t } = useI18n(["common"]);
  const [messageData, setMessageData] = useState(
    () => savedDraftMessage || getInitialDraftData()
  );
  const isEditMode = _.get(messageData, "isEditMode", false);

  const [isMoreMessagesLoading, setIsMoreMessagesLoading] = useState({
    next: false,
    previous: false,
  });

  const lastScrollPosition = useRef({
    scrollHeight: 0,
  });

  // debounce the updateDraftInRedux callback to avoid unnecessary redux updates
  // on every keystroke
  const [updateDraftInRedux] = useDebouncedCallback(
    (chatId, data) => {
      if (data) dispatch(updateDraft(chatId, data));
      else dispatch(clearDraft(chatId));
    },
    300,
    []
  );

  const addAttachmentsInMessageData = useCallback(
    (chatId, attachments) => {
      setMessageData(prev =>
        addAttachmentsInDraftState({ draft: prev, attachments })
      );
      // before updating attachments in redux, call any pending debounced callbacks.
      // to prevent any kind of race conditions
      updateDraftInRedux.flush?.();
      dispatch(addDraftAttachments(chatId, attachments));
    },
    [dispatch, updateDraftInRedux]
  );

  const onChangeMessageData = useCallback(
    (chatId, data) => {
      setMessageData(prev => ({ ...prev, ...data }));

      // if user is editing a message, reset the draft state in redux
      const isNextMessageEditMode = _.get(data, "isEditMode", isEditMode);
      updateDraftInRedux(chatId, isNextMessageEditMode ? null : data);
    },
    [isEditMode, updateDraftInRedux]
  );

  const syncDraftState = useEffectEvent(() => {
    // call any pending debounced callbacks.
    updateDraftInRedux.flush?.();

    // on change of chatId, update the local draft state with the saved draft state
    const messageData = savedDraftMessage || getInitialDraftData();
    setMessageData(messageData);
    if (_.isFunction(chatPanelRef?.current?.setChatbarContent)) {
      chatPanelRef.current.setChatbarContent(messageData.label);
    }
  });

  useEffect(() => {
    syncDraftState();
  }, [chatId, syncDraftState]);

  const handleLoadOldMessages = useCallback(
    async ({ pageSize } = {}) => {
      const scrollContainer = getChatContainer(chatId);
      try {
        lastScrollPosition.current.scrollHeight = scrollContainer?.scrollHeight;
        setIsMoreMessagesLoading(prev => ({ ...prev, previous: true }));
        await loadPreviousMoreMessages(pageSize);
        if (lastScrollPosition.current.scrollHeight) {
          if (!scrollContainer) return;
          scrollContainer.scrollTop =
            scrollContainer.scrollHeight -
            lastScrollPosition.current.scrollHeight;
          lastScrollPosition.current.scrollHeight = 0;
        }
      } catch (error) {
        // do nothing
      } finally {
        setIsMoreMessagesLoading(prev => ({ ...prev, previous: false }));
      }
    },
    [chatId, loadPreviousMoreMessages]
  );

  const handleLoadNewMessages = async () => {
    try {
      lastScrollPosition.current.scrollHeight = 0;
      setIsMoreMessagesLoading(prev => ({ ...prev, next: true }));
      await loadNextMoreMessages();
    } catch (error) {
      // do nothing
    } finally {
      setIsMoreMessagesLoading(prev => ({ ...prev, next: false }));
    }
  };

  const handleLoadEndMessagePage = useCallback(async () => {
    const scrollContainer = getChatContainer(chatId);
    if (hasNextPage) {
      //if we pass nothing here then it will call last page of current chat
      goToMessage();
    } else {
      scrollToWithAnimation(scrollContainer, scrollContainer?.scrollHeight);
    }
  }, [hasNextPage, chatId]);

  const updateMessageDataOnAction = useCallback(() => {
    // cancel any pending debounced callbacks and reset the local draft state
    updateDraftInRedux.cancel?.();
    setMessageData(getInitialDraftData());
  }, [updateDraftInRedux]);

  const handleCreateMessage = useCallback(
    async messageData => {
      updateMessageDataOnAction();

      // if user sent a message, when latest messages were not present in cache
      // reset the old messages, just append the new message sent by user, and fetchPrevious
      if (hasNextPage) {
        // Initiate loading UI as soon as user sends a message
        setIsMoreMessagesLoading(prev => ({ ...prev, previous: true }));
      }

      const messageType = _.get(messageData, "type", "");

      // to handle failed poll interaction message
      if (_.isEqual(messageType, MESSAGE_TYPE.INTERACTION)) {
        const attachment = _.get(messageData, "attachments", {});
        const firstAttachment = _.first(attachment);
        const interaction = _.get(firstAttachment, "interaction", {});

        const settings = {
          expiresAt: _.get(interaction, "expiresAt", ""),
          isMultipleVoteAllowed: _.get(
            interaction,
            "isMultipleVoteAllowed",
            false
          ),
          isResultPublic: _.get(interaction, "isResultPublic", false),
          isResponseEditAllowed: _.get(
            interaction,
            "isResponseEditAllowed",
            false
          ),
        };

        await dispatch(
          createPollMessage({
            chatId: _.get(messageData, "chatId"),
            settings,
            ...interaction,
          })
        );
      } else {
        await dispatch(createMessage(messageData));
      }
      // after createMessage is complete, fetch previous to avoid race conditions
      if (hasNextPage) {
        handleLoadOldMessages({ pageSize: PAGINATION_LIMITS.MESSAGES_FEED });
      }
    },
    [dispatch, hasNextPage, handleLoadOldMessages, updateMessageDataOnAction]
  );

  const handleGenerateSummary = async ({ after, before }) => {
    // if user generated a summary, when latest messages were not present in cache
    // reset the old messages, just append the new summary message gen by user,
    // and fetchPrevious
    if (hasNextPage) {
      // Initiate loading UI as soon as user sends a message
      setIsMoreMessagesLoading(prev => ({ ...prev, previous: true }));
    }
    await dispatch(generateSummary({ chatId, after, before }));
    // after generateSummary is complete, fetch previous to avoid race conditions
    if (hasNextPage) {
      handleLoadOldMessages({ pageSize: PAGINATION_LIMITS.MESSAGES_FEED });
    }
  };

  const handleUpdateMessageMutation = useCallback(
    messageData => {
      updateMessageDataOnAction();
      dispatch(updateMessage(messageData));
    },
    [dispatch, updateMessageDataOnAction]
  );

  const reversedConversations = useMemo(
    () => _.reverse(_.clone(conversations)),
    [conversations]
  );

  const isChat = chatType === CHAT_TYPE.CHAT;

  const isMessageOptionsEnabled =
    isChat || !_.includes(viewOnlyRoles, currentUserType);

  const isEmptyConversations = _.isEmpty(conversations);

  const { displayBannerMessage, displayBannerHeader } = getBannerMessage({
    bannerMessage,
    isChat,
    personalChatReceiverUser,
    isConnectEnabledForReceiver,
    t,
  });

  const showBannerMessage =
    (!_.isEmpty(displayBannerMessage) && !_.isNull(displayBannerMessage)) ||
    (!_.isEmpty(displayBannerHeader) && !_.isNull(displayBannerHeader));

  return (
    <>
      {_.isFunction(renderHeader) ? renderHeader(handleGenerateSummary) : null}
      {showBannerMessage ? (
        <div className={classes.bannerMessage}>
          <div className={classes.bannerIcon}>
            <InformationFilled variant="warning" size="xx-small" />
          </div>

          <div className={classes.bannerInfoContainer}>
            {displayBannerHeader ? (
              <div className="text-label">{displayBannerHeader}</div>
            ) : null}
            {displayBannerMessage ? (
              <ConditionalWrapper
                wrapper={<Tooltip tooltip={displayBannerMessage} />}
                condition={
                  _.size(displayBannerMessage) > MAX_BANNER_MESSAGE_LENGTH
                }
                type={"wrapperAsElement"}
              >
                <div className="text-body">
                  {_.truncate(displayBannerMessage, {
                    length: MAX_BANNER_MESSAGE_LENGTH,
                  })}
                </div>
              </ConditionalWrapper>
            ) : null}
          </div>
        </div>
      ) : null}
      {isEmptyConversations ? (
        <EmptyChatContainer
          users={otherUsers}
          chatType={chatType}
          channelLabel={channelLabel}
          description={description}
        />
      ) : (
        <MessagesContainer
          chatId={chatId}
          chatType={chatType}
          users={users}
          chatPanelRef={chatPanelRef}
          viewOnlyUsers={viewOnlyUsers}
          isArchived={isArchived || isArchivedChatDetail}
          conversations={reversedConversations}
          onParentMessageClick={goToMessage}
          lastReadMessage={lastReadMessage}
          handleLoadNewMessages={handleLoadNewMessages}
          handleLoadOldMessages={handleLoadOldMessages}
          isMoreMessagesLoading={isMoreMessagesLoading}
          hasNextPage={hasNextPage}
          hasPreviousPage={hasPreviousPage}
          handleCreateMessage={handleCreateMessage}
          highlightedMessageId={highlightedMessageId}
          onChangeMessageData={onChangeMessageData}
          isMessageOptionsEnabled={isMessageOptionsEnabled}
          unreadMessageCount={unreadMessageCount}
          handleLoadEndMessagePage={handleLoadEndMessagePage}
          isUserCreator={isUserCreator}
          personalChatReceiverUserId={_.get(personalChatReceiverUser, "id")}
        />
      )}
      {!isViewMode ? (
        <Footer
          isArchived={isArchived || isArchivedChatDetail}
          archivedBy={archivedBy}
          viewOnlyRoles={viewOnlyRoles}
          viewOnlyUsers={viewOnlyUsers}
          chatType={chatType}
          chatId={chatId}
          users={users}
          chatPanelRef={chatPanelRef}
          lastActiveAt={lastActiveAt}
          screenType={screenType}
          isEmptyConversations={isEmptyConversations}
          onParentMessageClick={goToMessage}
          handleCreateMessage={handleCreateMessage}
          handleGenerateSummary={handleGenerateSummary}
          messageData={messageData}
          onChangeMessageData={onChangeMessageData}
          addAttachmentsInMessageData={addAttachmentsInMessageData}
          handleUpdateMessageMutation={handleUpdateMessageMutation}
          conversationType={conversationType}
          isUserCreator={isUserCreator}
          createdBy={createdBy}
          isPersonalChatPaid={isPersonalChatPaid}
          channelLabel={channelLabel}
        />
      ) : null}
      {isSearchingInOpenedChat ? (
        <div className={classes.searchingLoader}>
          <Loading />
        </div>
      ) : null}
    </>
  );
};

MessagesAndInputContainer.propTypes = {
  chatType: PropTypes.oneOf(_.values(CHAT_TYPE)).isRequired,
  screenType: PropTypes.string, // to show footer in search screen
  otherUsers: PropTypes.arrayOf(PropTypes.object).isRequired,
  isArchived: PropTypes.bool.isRequired,
  conversations: PropTypes.arrayOf(PropTypes.object).isRequired,
  goToMessage: PropTypes.func.isRequired,
  highlightedMessageId: PropTypes.string,
  channelLabel: PropTypes.string,
  savedDraftMessage: PropTypes.object,
  renderHeader: PropTypes.func,
};

MessagesAndInputContainer.defaultProps = {
  highlightedMessageId: null,
  renderHeader: null,
};

const mapStateToProps = (state, ownProps) => {
  const { chatId } = ownProps;
  const messagingReduxState = getMessagingV2ReduxState(state);
  const currentUserType = getUserType(state);
  const savedDraftMessage = _.get(messagingReduxState, ["drafts", chatId]);
  const isSearchingInOpenedChat = _.get(
    messagingReduxState,
    "isSearchingInOpenedChat",
    false
  );
  return { savedDraftMessage, isSearchingInOpenedChat, currentUserType };
};

export default compose(
  connect(mapStateToProps),
  getBothWaysMessagesEnhancer(),
  withLoader
)(MessagesAndInputContainer);
